import React from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { REPORT_THEME } from "../reportTheme";

const SUCCESS_COLOR = "#10B981";
const DANGER_COLOR = "#EF4444";

const ChartContainer = ({ title, data }) => {
  const filled = data?.filledCount || data?.uploadedCount || 0;
  const missing = data?.missingCount || 0;
  const total = filled + missing;
  const completionRate = total > 0 ? Math.round((filled / total) * 100) : 0;

  const chartData = [
    { name: "Completed", value: filled },
    { name: "Missing", value: missing },
  ].filter(() => total > 0);

  if (total === 0) {
    return (
      <div
        className={`bg-white rounded-xl p-5 border shadow-sm flex flex-col h-full min-h-[250px] ${REPORT_THEME.section.wrap}`}
      >
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-auto">
          {title}
        </h3>
        <div className="flex-1 flex items-center justify-center">
          <span className="text-slate-400 text-xs">No Data Available</span>
        </div>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const { name, value } = payload[0];
      const color = name === "Completed" ? SUCCESS_COLOR : DANGER_COLOR;

      return (
        <div className="bg-white px-3 py-2 rounded-lg shadow-lg border border-indigo-100 flex items-center gap-2">
          <div
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: color }}
          />
          <span className="text-xs font-semibold text-slate-700">
            {name}: {value}
          </span>
        </div>
      );
    }
    return null;
  };

  return (
    <div
      className={`bg-white rounded-xl p-5 border shadow-sm flex flex-col h-full transition-all duration-200 hover:shadow-md hover:border-indigo-300 ${REPORT_THEME.section.wrap}`}
    >
      <div className="mb-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {title}
        </h3>
      </div>

      <div className="relative flex-1 min-h-[160px] flex items-center justify-center">
        <ResponsiveContainer width="100%" height={160}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={55}
              outerRadius={70}
              paddingAngle={5}
              dataKey="value"
              cornerRadius={4}
              startAngle={90}
              endAngle={-270}
              stroke="none"
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={
                    entry.name === "Completed" ? SUCCESS_COLOR : DANGER_COLOR
                  }
                />
              ))}
            </Pie>
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: "transparent" }}
            />
          </PieChart>
        </ResponsiveContainer>

        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-2xl font-bold text-slate-900 tabular-nums">
            {completionRate}%
          </span>
        </div>
      </div>

      <div className="flex items-center justify-between text-xs mt-4 pt-3 border-t border-indigo-50">
        <div className="flex flex-col items-center flex-1 border-r border-indigo-50 last:border-0">
          <span className="font-bold text-emerald-600 text-lg tabular-nums">
            {filled}
          </span>
          <span className="text-[10px] text-slate-400 font-medium uppercase">
            Done
          </span>
        </div>
        <div className="flex flex-col items-center flex-1">
          <span className="font-bold text-red-500 text-lg tabular-nums">
            {missing}
          </span>
          <span className="text-[10px] text-slate-400 font-medium uppercase">
            Missing
          </span>
        </div>
      </div>
    </div>
  );
};

const ComplianceCharts = ({ data }) => {
  if (!data || !data.statistics) return null;
  const { applicationForms, medicalForms, credentialsForUpload, taxForms } =
    data.statistics;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
      <ChartContainer title="Application Forms" data={applicationForms} />
      <ChartContainer title="Medical Forms" data={medicalForms} />
      <ChartContainer title="Tax Forms" data={taxForms} />
      <ChartContainer title="Credentials" data={credentialsForUpload} />
    </div>
  );
};

export default ComplianceCharts;
